import { IColumn, IFetchXmlDataProviderColumn } from "../dataset";
import { Attribute } from "../metadata";
import { Constants } from "./Constants";
import { Direction } from "./Direction";
import { FetchXmlBuilder } from "./FetchXmlBuilder";

export class FetchXmlParser {
    private _doNotIncludeFetchXmlColumns: boolean = false;
    constructor() {
    }
    /**
    * Parses a FetchXML query string and extracts metadata about the query.
    * @param {boolean} [doNotIncludeFetchXmlColumns=false] - If true, the resulting columns will only include columns that were passed in the `columns` parameter. Otherwise the resulting columns also include all atributes that are defined in passed fetchXml.
    */
    public async parse(fetchXml: string, columns?: IFetchXmlDataProviderColumn[], doNotIncludeFetchXmlColumns?: boolean): Promise<{
        savedqueryid: string | null,
        userqueryid: string | null
        entityName: string;
        pageSize: number;
        pageNumber: number;
        linkedEntities: ComponentFramework.PropertyHelper.DataSetApi.LinkEntityExposedExpression[],
        columns: IColumn[],
        sortStatus: ComponentFramework.PropertyHelper.DataSetApi.SortStatus[],
    }> {
        this._doNotIncludeFetchXmlColumns = !!doNotIncludeFetchXmlColumns;
        const fetch = FetchXmlBuilder.fetch.fromXml(fetchXml);
        const linkedEntities = this.getLinkedEntities(fetch);
        const attributes = this._getAttributes(fetch, linkedEntities, columns);
        const orders = this._getOrders(fetch, linkedEntities);
        const fetchXmlColumns = await this._mapAttributesToColumns(fetch, linkedEntities, attributes);

        return {
            pageSize: fetch.count || 15,
            pageNumber: fetch.page || 1,
            savedqueryid: fetch.savedqueryid || null,
            userqueryid: fetch.userqueryid || null,
            entityName: fetch.entity.name,
            linkedEntities: linkedEntities.map(x => {
                return {
                    alias: x.alias,
                    from: x.from,
                    linkType: x.linktype,
                    name: x.name,
                    to: x.to
                };
            }),
            columns: this._parseColumns(fetchXmlColumns, columns),
            sortStatus: orders.map(x => {
                const name = x.alias ? `${x.alias}.${x.attribute}` : x.attribute;
                return {
                    name: name,
                    sortDirection: x.descending ? Direction.Desc : Direction.Asc,
                };
            }),
        };
    }

    /**
     * Return an array of all linked entities in fetchXml (including nested linked entities);
     */
    public getLinkedEntities(fetch: FetchXmlBuilder.fetch): FetchXmlBuilder.linkEntity[] {
        let linkedEntities = fetch.entity.linkEntities;
        const getLinkedEntitiesRecursively = (linkedEntity: FetchXmlBuilder.linkEntity) => {
            linkedEntities = [...linkedEntities, ...linkedEntity.linkEntities];
            linkedEntity.linkEntities.map(x => getLinkedEntitiesRecursively(x));
        };
        linkedEntities.map(x => getLinkedEntitiesRecursively(x));
        return linkedEntities;

    }
    private _parseColumns(fetchXmlColumns: IColumn[], columns?: IFetchXmlDataProviderColumn[]): IColumn[] {
        if (!columns) {
            return fetchXmlColumns;
        }
        fetchXmlColumns = fetchXmlColumns.map(x => {
            const column = columns?.find(y => y.name === x.name);
            if (!column) {
                return x;
            }
            //update the fetchXmlColumns with information from the provided columns
            return {
                ...x,
                ...column,
                //show the defined columns by default
                isHidden: column.isHidden ?? false
            };
        });
        columns.filter(x => !fetchXmlColumns.map(y => y.name).includes(x.name)).map((col) => {
            //add missing columns to fetchXmlColumns => these columns will not have any value, but their value can be set via setValue and retrieve via getValue
            fetchXmlColumns.push({ ...col as IColumn, isHidden: col.isHidden ?? false });
        });

        const idIndexMap = new Map<string, number>();
        columns.map((cell, index) => {
            idIndexMap.set(cell.name, index);
        });
        fetchXmlColumns.sort((a, b) => {
            const aIndex = idIndexMap.has(a.name) ? idIndexMap.get(a.name)! : columns.length;
            const bIndex = idIndexMap.has(b.name) ? idIndexMap.get(b.name)! : columns.length;
            return aIndex - bIndex;
        });
        return fetchXmlColumns.map((col, i) => {
            col.order = col.isHidden ? - 1 : i + 1;
            return col;
        });
    }
    private _getOrders(fetch: FetchXmlBuilder.fetch, linkedEntities: FetchXmlBuilder.linkEntity[]): FetchXmlBuilder.order[] {
        let orders = fetch.entity.orders;
        orders = [...orders, ...linkedEntities.flatMap(x => {
            x.orders.map(y => y.alias = x.alias);
            return x.orders;
        })];
        return orders;
    }

    private _getAttributes(fetch: FetchXmlBuilder.fetch, linkedEntities: FetchXmlBuilder.linkEntity[], columns?: IFetchXmlDataProviderColumn[]): string[] {
        let attributes: string[] = [];
        if (!this._doNotIncludeFetchXmlColumns) {
            attributes = fetch.entity.attributes.map(x => x.name);
            attributes = [...attributes, ...linkedEntities.flatMap(linkedEntity => {
                return linkedEntity.attributes.map(attr => `${linkedEntity.alias}.${attr.name}`);
            })];
        }
        if (columns) {
            const columnNames = columns.map(x => x.name);
            attributes = [...attributes, ...columnNames];
        }
        return [...new Set(attributes)];
    }
    private async _mapAttributesToColumns(fetch: FetchXmlBuilder.fetch, linkedEntities: FetchXmlBuilder.linkEntity[], attributes: string[]): Promise<IColumn[]> {
        attributes = attributes.filter(x => x !== Constants.RIBBON_BUTTONS_COLUMN_NAME && !x.endsWith('__virtual'));
        return await Promise.all(attributes.map(async (x, i) => {
            const isLinkedEntityColumn = Attribute.GetLinkedEntityAlias(x);
            const entityName = isLinkedEntityColumn ? linkedEntities.find(y => y.alias === Attribute.GetLinkedEntityAlias(x))!.name : fetch.entity.name;
            const attributeName = Attribute.GetNameFromAlias(x);
            const entityMetadata = await window.Xrm.Utility.getEntityMetadata(entityName, [attributeName]);
            const attributeMetadata = entityMetadata.Attributes.get(attributeName);
            const displayName = attributeMetadata.DisplayName;
            const linkedEntityAlias = Attribute.GetLinkedEntityAlias(x);
            return {
                alias: x,
                dataType: Attribute.GetDataTypeFromMetadata(attributeMetadata),
                disableSorting: false,
                displayName: isLinkedEntityColumn ? `${displayName} (${entityMetadata.DisplayName})` : displayName,
                isHidden: true,
                isPrimary: !isLinkedEntityColumn && entityMetadata.PrimaryNameAttribute === attributeName,
                name: x,
                order: i,
                visualSizeFactor: 300,
                //@ts-ignore - legacy attribute, not needed, alias can be fetched from column name and can be used to find the entity name in linked entities
                __relatedEntityName: linkedEntities.find(x => x.alias === linkedEntityAlias)?.name ?? ""
            };
        }));
    }
}